<template>
  <div class="matrix-chart-container" ref="chartContainer">
    <canvas 
      ref="lineChart" 
      class="chart-canvas"
      @mousemove="handleMouseMove"
      @mouseleave="handleMouseLeave"
    ></canvas>

    <!-- 右上角动态文字 -->
    <div v-if="animationFinished && topRightText" class="top-right-text" :style="{ opacity: textDisplayProgress }">
      {{ topRightText }}
    </div>

    <!-- 鼠标悬停提示框（只显示时间） -->
    <!-- <div 
      v-if="showTooltip && hoveredData" 
      class="chart-tooltip"
      :style="tooltipStyle"
    >
      <div class="tooltip-date">{{ hoveredData.label }}</div>
    </div> -->
  </div>
</template>

<script>
export default {
  name: 'GradientLineChart',
  props: {
    // 数据源
    dataList: {
      type: Array,
      required: false,
      default: () => [],
      validator: (value) => Array.isArray(value)
    },
    // X轴首尾展示标签
    xAxisFirstLabel: { type: String, required: true },
    xAxisLastLabel: { type: String, required: true },
    // 数据取值字段
    valueField: { type: String, default: 'value' },
    // 其他原有属性
    animationDuration: { type: Number, default: 2500 },
    lineColor: { type: String, default: '#FFD700' },
    gradientColor: { type: String, default: '#FFD700' },
    topRightText: { type: String, default: '' },
    aspectRatio: { type: Number, default: 2.0 },
    intersectionThreshold: { type: Number, default: 0.3 }
  },
  data() {
    return {
      ctx: null,
      canvas: null,
      maxVal: 0,
      minVal: 0,
      padding: { top: 20, right: 35, bottom: 40, left: 35 },
      animationProgress: 0,
      animationFrameId: null,
      startTime: null,
      pointPositions: [],
      yTicks: [],
      animationFinished: false,
      textDisplayProgress: 0,
      textAnimationFrameId: null,
      isInViewport: false,
      intersectionObserver: null,
      showTooltip: false,
      hoveredData: null,
      tooltipStyle: { left: '0px', top: '0px' },
      canvasRect: null,
      now: new Date(),
      monthsAgo24: new Date()
    };
  },
  mounted() {
    this.initCanvas();
    this.initIntersectionObserver();
    setTimeout(() => {
      this.resizeCanvas();
      window.addEventListener('resize', this.handleResize);
      this.canvas && this.updateCanvasRect();
    }, 100);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    this.animationFrameId && cancelAnimationFrame(this.animationFrameId);
    this.textAnimationFrameId && cancelAnimationFrame(this.textAnimationFrameId);
    if (this.intersectionObserver && this.$refs.chartContainer) {
      this.intersectionObserver.unobserve(this.$refs.chartContainer);
      this.intersectionObserver.disconnect();
    }
  },
  watch: {
    dataList: {
      handler(newVal) {
        if (newVal?.length && this.isInViewport) this.resetAndRestartAnimation();
        else {
          this.resetAnimationState();
          this.ctx && this.canvas && this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        }
      },
      deep: true,
      immediate: false
    },
    isInViewport: {
      handler(newVal) {
        if (newVal && this.canvas && this.dataList?.length) this.resetAndRestartAnimation();
        else newVal || this.resetAnimationState();
      }
    }
  },
  methods: {
    updateCanvasRect() {
      this.canvasRect = this.canvas.getBoundingClientRect();
    },
    handleMouseMove(event) {
      if (!this.animationFinished || !this.pointPositions?.length || !this.canvas) return;
      
      this.updateCanvasRect();
      const mouseX = event.clientX - this.canvasRect.left;
      const mouseY = event.clientY - this.canvasRect.top;
      
      const inArea = mouseX >= this.padding.left && mouseX <= this.canvas.width - this.padding.right
        && mouseY >= this.padding.top && mouseY <= this.canvas.height - this.padding.bottom;
      if (!inArea) return this.showTooltip = false;
      
      let minDist = Infinity;
      let nearestIdx = -1;
      this.pointPositions.forEach((p, i) => {
        const dist = Math.abs(mouseX - p.x);
        if (dist < minDist && dist < 30) {
          minDist = dist;
          nearestIdx = i;
        }
      });
      
      if (nearestIdx !== -1 && this.dataList?.[nearestIdx]) {
        this.hoveredData = {
          label: this.dataList[nearestIdx].label || `数据${nearestIdx + 1}`,
          value: this.dataList[nearestIdx]?.[this.valueField] || 0
        };
        this.showTooltip = true;
        
        const tipW = 100, tipH = 40;
        let left = mouseX + 5;
        let top = mouseY - 5;
        left + tipW > this.canvas.width && (left = mouseX - tipW - 5);
        left < 0 && (left = 5);
        top < 0 && (top = mouseY + 5);
        top + tipH > this.canvas.height && (top = this.canvas.height - tipH - 5);
        this.tooltipStyle = { left: `${left}px`, top: `${top}px` };
      } else this.showTooltip = false;
    },
    handleMouseLeave() {
      this.showTooltip = false;
      this.hoveredData = null;
    },
    initIntersectionObserver() {
      if (!window.IntersectionObserver) return this.isInViewport = true;
      
      this.intersectionObserver = new IntersectionObserver(
        entries => entries.forEach(e => this.isInViewport = e.isIntersecting && e.intersectionRatio >= this.intersectionThreshold),
        { threshold: this.intersectionThreshold, rootMargin: '0px 0px -50px 0px' }
      );
      this.$refs.chartContainer && this.intersectionObserver.observe(this.$refs.chartContainer);
    },
    resetAnimationState() {
      this.animationFrameId && cancelAnimationFrame(this.animationFrameId);
      this.textAnimationFrameId && cancelAnimationFrame(this.textAnimationFrameId);
      this.animationProgress = 0;
      this.animationFinished = false;
      this.textDisplayProgress = 0;
      this.showTooltip = false;
      this.hoveredData = null;
      this.ctx && this.canvas && this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    },
    initCanvas() {
      this.canvas = this.$refs.lineChart;
      this.canvas && (this.ctx = this.canvas.getContext('2d'));
    },
    resizeCanvas() {
      if (!this.canvas) return;
      const container = this.canvas.parentElement;
      if (!container) return;
      
      const w = container.clientWidth;
      const h = w / this.aspectRatio;
      container.style.height = `${h}px`;
      this.canvas.width = w;
      this.canvas.height = h;
      
      this.updateCanvasRect();
      if (this.isInViewport && this.dataList?.length) {
        this.initData();
        this.drawStatic();
      } else this.ctx && this.ctx.clearRect(0, 0, w, h);
    },
    initData() {
      if (!this.dataList?.length) {
        this.maxVal = this.minVal = 0;
        this.yTicks = [];
        return;
      }
      
      const firstItem = this.dataList[0];
      if (!firstItem || !firstItem.hasOwnProperty(this.valueField)) {
        console.warn(`数据缺少字段：${this.valueField}`);
        this.maxVal = this.minVal = 0;
        this.yTicks = [];
        return;
      }
      
      const values = this.dataList.map(item => {
        const val = item[this.valueField];
        return typeof val === 'number' ? val : 0;
      }).filter(val => !isNaN(val));
      
      if (!values.length) {
        this.maxVal = this.minVal = 0;
        this.yTicks = [];
        return;
      }
      
      this.maxVal = Math.max(...values) * 1.1;
      this.minVal = Math.min(...values);
      this.yTicks = this.calculateYTicks();
    },
    calculateYTicks() {
      const range = this.maxVal - this.minVal;
      if (range === 0) return [this.minVal - 1, this.minVal, this.minVal + 1, this.minVal + 2, this.minVal + 3];
      
      const tickCount = 4;
      const step = range / tickCount;
      return Array.from({ length: tickCount + 1 }, (_, i) => Math.round((this.minVal + step * i) * 100) / 100);
    },
    valueToY(val) {
      const chartH = this.canvas.height - this.padding.top - this.padding.bottom;
      const range = this.maxVal - this.minVal || 1;
      return this.padding.top + chartH - ((val - this.minVal) / range) * chartH;
    },
    indexToX(index) {
      if (!this.dataList?.length) return this.padding.left;
      const chartW = this.canvas.width - this.padding.left - this.padding.right;
      const numPoints = this.dataList.length;
      return numPoints > 1 ? this.padding.left + (index / (numPoints - 1)) * chartW : this.padding.left;
    },
    // 核心修改：刻度线与数据点数量对齐
    drawStatic() {
      if (!this.ctx || !this.canvas) return;
      const ctx = this.ctx;
      ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      
      // 绘制X轴横线
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
      ctx.beginPath();
      ctx.moveTo(this.padding.left, this.canvas.height - this.padding.bottom);
      ctx.lineTo(this.canvas.width - this.padding.right, this.canvas.height - this.padding.bottom);
      ctx.stroke();
      
      if (this.dataList?.length) {
        // 动态字体大小
        const baseW = 800, baseFS = 14;
        const scale = this.canvas.width / baseW;
        const fs = Math.max(10, Math.min(16, baseFS * scale));
        ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
        ctx.font = `${fs}px sans-serif`;
        ctx.textBaseline = 'top';
        
        // 绘制首尾标签
        const firstX = this.indexToX(0);
        const lastX = this.indexToX(this.dataList.length - 1);
        const textY = this.canvas.height - this.padding.bottom / 2;
        ctx.textAlign = 'left';
        ctx.fillText(this.xAxisFirstLabel, firstX, textY);
        ctx.textAlign = 'right';
        ctx.fillText(this.xAxisLastLabel, lastX, textY);
        
        // 核心调整：刻度线数量与数据点对齐（最多24个）
        const numPoints = this.dataList.length;
        const numIntervals = Math.min(numPoints, 24); // 数据≤24时，每个数据点对应1条刻度
        for (let i = 0; i < numIntervals; i++) {
          const x = this.indexToX(i); // 刻度X坐标与对应数据点X坐标一致
          
          // 刻度样式：首尾更突出
          ctx.strokeStyle = i === 0 || i === numIntervals - 1 
            ? 'rgba(255, 255, 255, 0.7)' 
            : 'rgba(255, 255, 255, 0.3)';
          ctx.beginPath();
          ctx.moveTo(x, this.canvas.height - this.padding.bottom);
          ctx.lineTo(x, this.canvas.height - this.padding.bottom + (i === 0 || i === numIntervals - 1 ? 8 : 4));
          ctx.stroke();
        }
      }
    },
    animate() {
      if (!this.dataList?.length) {
        this.animationFinished = false;
        return;
      }
      
      const now = performance.now();
      this.animationProgress = Math.min((now - this.startTime) / this.animationDuration, 1);
      this.drawStatic();
      this.drawDynamicLineAndGradient();
      
      if (this.animationProgress < 1) this.animationFrameId = requestAnimationFrame(this.animate);
      else {
        this.animationFinished = true;
        this.topRightText && this.startTextAnimation();
      }
    },
    startTextAnimation() {
      const duration = 1000;
      const start = performance.now();
      const animate = () => {
        this.textDisplayProgress = Math.min((performance.now() - start) / duration, 1);
        this.textDisplayProgress < 1 && (this.textAnimationFrameId = requestAnimationFrame(animate));
      };
      this.textAnimationFrameId = requestAnimationFrame(animate);
    },
    drawDynamicLineAndGradient() {
      // 修复：先判空并获取this.ctx
      if (!this.ctx || !this.dataList?.length || this.animationProgress === 0) return;
      const ctx = this.ctx; // 先赋值给局部变量，避免重复写this.ctx
      
      const numPoints = this.dataList.length;
      const totalSeg = numPoints - 1;
      const progToPoint = this.animationProgress * totalSeg;
      const currIdx = Math.min(Math.floor(progToPoint), totalSeg);
      const segProg = progToPoint - currIdx;
      
      // 计算数据点位置
      this.pointPositions = this.dataList.map((item, i) => ({
        x: this.indexToX(i),
        y: this.valueToY(item?.[this.valueField] || 0),
        label: item?.label || `数据${i + 1}`,
        value: item?.[this.valueField] || 0
      }));
      
      // 绘制渐变
      if (currIdx >= 0 && this.pointPositions.length) {
        ctx.beginPath();
        ctx.moveTo(this.pointPositions[0].x, this.canvas.height - this.padding.bottom);
        for (let i = 0; i <= currIdx; i++) ctx.lineTo(this.pointPositions[i].x, this.pointPositions[i].y);
        if (currIdx < totalSeg) {
          const sX = this.pointPositions[currIdx].x;
          const sY = this.pointPositions[currIdx].y;
          const eX = this.pointPositions[currIdx + 1]?.x || sX;
          const eY = this.pointPositions[currIdx + 1]?.y || sY;
          ctx.lineTo(sX + (eX - sX) * segProg, sY + (eY - sY) * segProg);
        }
        ctx.lineTo(this.pointPositions[currIdx < totalSeg ? currIdx + 1 : currIdx].x, this.canvas.height - this.padding.bottom);
        ctx.closePath();
        const grad = ctx.createLinearGradient(0, 0, 0, this.canvas.height);
        grad.addColorStop(0, `${this.gradientColor}80`);
        grad.addColorStop(1, `${this.gradientColor}00`);
        ctx.fillStyle = grad;
        ctx.fill();
      }
      
      // 绘制折线
      if (currIdx >= 0 && this.pointPositions.length) {
        ctx.beginPath();
        ctx.moveTo(this.pointPositions[0].x, this.pointPositions[0].y);
        for (let i = 1; i <= currIdx; i++) ctx.lineTo(this.pointPositions[i].x, this.pointPositions[i].y);
        if (currIdx < totalSeg) {
          const sX = this.pointPositions[currIdx].x;
          const sY = this.pointPositions[currIdx].y;
          const eX = this.pointPositions[currIdx + 1]?.x || sX;
          const eY = this.pointPositions[currIdx + 1]?.y || sY;
          ctx.lineTo(sX + (eX - sX) * segProg, sY + (eY - sY) * segProg);
        }
        ctx.strokeStyle = this.lineColor;
        ctx.lineWidth = 3;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.stroke();
      }
      
      // 绘制数据点
      if (this.pointPositions.length) {
        ctx.fillStyle = this.lineColor;
        for (let i = 0; i <= currIdx; i++) {
          const p = this.pointPositions[i];
          if (p) { // 增加判空，避免空指针
            ctx.beginPath();
            ctx.arc(p.x, p.y, 5, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }
    },
    resetAndRestartAnimation() {
      if (!this.dataList?.length) return;
      this.resetAnimationState();
      this.initData();
      this.drawStatic();
      this.startTime = performance.now();
      this.animationFrameId = requestAnimationFrame(this.animate);
    },
    handleResize() {
      this.resizeCanvas();
      this.updateCanvasRect();
      this.isInViewport && this.dataList?.length && this.resetAndRestartAnimation();
    }
  }
};
</script>

<style scoped>
.matrix-chart-container {
  width: 100%;
  position: relative;
  background: #171524;
  border-radius: 8px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
  overflow: visible;
  margin: 0 auto;
  cursor: crosshair;
}
.chart-canvas { display: block; width: 100%; height: 100%; }
.top-right-text {
  position: absolute;
  top: 20px;
  right: 20px;
  color: #eee;
  font-size: 16px;
  font-weight: 500;
  opacity: 0;
  transition: opacity 0.5s ease-out;
  z-index: 5;
}
.chart-tooltip {
  position: absolute;
  background: rgba(0, 0, 0, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  padding: 8px 12px;
  color: white;
  font-size: 13px;
  pointer-events: none;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  min-width: 80px;
  backdrop-filter: blur(8px);
  text-align: center;
}
.tooltip-date { font-weight: 600; color: #FFD700; font-size: 12px; }
</style>